import React, { useMemo } from 'react';

import moment from 'moment';

import Shimmer from 'components/Shimmer';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { VehicleOption } from 'models/auto/vehicle';
import { getFormattedPrice } from 'utils/currency';

import { Header, Info, Address, IconPickUp, IconDropOff } from './styles';

export interface Props {
  vehicle: VehicleOption;
  isPickUp?: boolean;
  isSelected?: boolean;
}

export const LoadingAutoAgency = () => (
  <>
    <Header>
      <Header.Title>
        <Shimmer>Nome da Agência</Shimmer>
      </Header.Title>
    </Header>
    <Info>
      <Address>
        <Address.Item>
          <Shimmer>Av. Paulista, 1200 - cj. 12 - Bela Vista - SP</Shimmer>
        </Address.Item>
        <Address.Item>
          <Shimmer>Av. Paulista, 1200 - cj. 12 - Bela Vista - SP</Shimmer>
        </Address.Item>
      </Address>
      <Address.Price>
        <strong>
          <Shimmer>$100,000</Shimmer>
        </strong>
        <Address.Period>
          <Shimmer>Preço final x 1 dia</Shimmer>
        </Address.Period>
      </Address.Price>
    </Info>
  </>
);

const AutoAgency = ({ vehicle, isPickUp = true, isSelected = false }: Props) => {
  const { t } = useStoreTranslation();
  const currencyCode = useMemo(
    () =>
      vehicle.rates && vehicle.rates[0] && vehicle.rates[0].pricePayNow
        ? vehicle.rates[0].pricePayNow?.currencyCode
        : '',
    [vehicle.rates],
  );

  const period = useMemo(() => {
    const days = moment(vehicle.dropOff?.time).diff(moment(vehicle.pickUp?.time), 'days');
    return days > 1
      ? `${t('autosDetails.agencies.finalPriceDays', { days: days })}`
      : `${t('autosDetails.agencies.finalPriceDay')}`;
  }, [vehicle.dropOff?.time, vehicle.pickUp?.time, t]);

  const agencyName = useMemo(() => {
    const address = isPickUp ? vehicle.pickUp?.address : vehicle.dropOff?.address;
    const comma = address?.indexOf(',') === -1 ? '/' : ',';
    return address?.substring(address.lastIndexOf(comma) + 1, address.length);
  }, [isPickUp, vehicle.dropOff?.address, vehicle.pickUp?.address]);

  const formattedPrice = useMemo(() => {
    const total: number =
      vehicle.rates && vehicle.rates[0] && vehicle.rates[0].pricePayNow && vehicle.rates[0].pricePayNow.total
        ? vehicle.rates[0].pricePayNow.total
        : 0;
    const { formatted } = getFormattedPrice({ value: total, currency: currencyCode, t });
    return formatted;
  }, [vehicle.rates, currencyCode, t]);

  return (
    <>
      <Header>
        <Header.Title>
          {t('autosDetails.agencies.agencyName')} {agencyName}
        </Header.Title>
        {isSelected ? <Header.Selected>{t('autosDetails.agencySelected')}</Header.Selected> : null}
      </Header>
      <Info>
        <Address>
          <Address.Item>
            <Address.Icon>
              <IconPickUp />
            </Address.Icon>{' '}
            {vehicle.pickUp?.address}
          </Address.Item>
          {vehicle.pickUp?.address !== vehicle.dropOff?.address ? (
            <Address.Item>
              <Address.Icon>
                <IconDropOff />
              </Address.Icon>{' '}
              {vehicle.dropOff?.address}
            </Address.Item>
          ) : null}
        </Address>
        <Address.Price>
          <strong>{formattedPrice}</strong>
          <Address.Period>{period}</Address.Period>
        </Address.Price>
      </Info>
    </>
  );
};

export default AutoAgency;
